// SkyTracker @skytracker/web — WebSocket client with reconnect + heartbeat.
import type {
  Bbox,
  ClientFilters,
  FeedStatus,
  Position,
  ServerMessage,
} from '@skytracker/shared';

export interface WsHandlers {
  onSnapshot: (positions: Position[], feed: FeedStatus[], truncated: { count: number; sampled_by: 'altitude' } | null) => void;
  onUpdate:   (positions: Position[]) => void;
  onRemove:   (ids: string[]) => void;
  onStatus:   (feed: FeedStatus[]) => void;
  onOpen?:    () => void;
  onClose?:   () => void;
}

const BACKOFF_STEPS = [250, 500, 1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

export class SkyTrackerWs {
  private ws: WebSocket | null = null;
  private bbox: Bbox | null = null;
  private filters: ClientFilters | undefined;
  private retryIdx = 0;
  private closed = false;

  constructor(private readonly url: string, private readonly h: WsHandlers) {}

  start(): void {
    this.connect();
  }

  stop(): void {
    this.closed = true;
    this.ws?.close();
  }

  subscribe(bbox: Bbox, filters: ClientFilters): void {
    this.bbox = bbox;
    this.filters = filters;
    this.send({ type: 'subscribe', bbox, filters });
  }

  refresh(): void {
    this.send({ type: 'refresh' });
  }

  private send(msg: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private connect(): void {
    if (this.closed) return;
    const ws = new WebSocket(this.url);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.retryIdx = 0;
      this.h.onOpen?.();
      if (this.bbox) this.send({ type: 'subscribe', bbox: this.bbox, filters: this.filters });
    });

    ws.addEventListener('message', (ev) => {
      let parsed: ServerMessage;
      try {
        parsed = JSON.parse(ev.data as string) as ServerMessage;
      } catch {
        return;
      }
      switch (parsed.type) {
        case 'snapshot':
          this.h.onSnapshot(parsed.positions, parsed.feed_status, parsed.truncated ?? null);
          break;
        case 'update':
          this.h.onUpdate(parsed.positions);
          break;
        case 'remove':
          this.h.onRemove(parsed.ids);
          break;
        case 'status':
          this.h.onStatus(parsed.feed_status);
          break;
        case 'ping':
          this.send({ type: 'pong', t: parsed.t });
          break;
        case 'error':
          console.warn('[ws] server error', parsed);
          break;
      }
    });

    ws.addEventListener('close', () => {
      this.h.onClose?.();
      this.ws = null;
      if (this.closed) return;
      const delay = BACKOFF_STEPS[Math.min(this.retryIdx, BACKOFF_STEPS.length - 1)] ?? 30_000;
      this.retryIdx += 1;
      setTimeout(() => this.connect(), delay);
    });

    ws.addEventListener('error', () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    });
  }
}
